import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { subscribeToNewsletter, unsubscribeToNewsletter } from "./Api"; // Update the import path accordingly
import { useTranslation } from "react-i18next";
import { Header, Icon, Button } from "semantic-ui-react";
import "../style.scss";
import { DashboardRoute } from "@/routes/dashboard";
import { saveResponseInLocalStorage, getUserId } from "@/api/localStorage";
import { getShortReminders } from "@/api/reminder";
import { Spinner } from "@/shared/components";
import { SessionStorage } from "../../Common/Utilities/storage";

const UnsubscribeMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Header as="h2" icon>
      <Icon name="checkmark" color="green" size="massive" />
      {t("newsletter.successPage.unsubscribe.title")}
      <Header.Subheader>{t("newsletter.successPage.unsubscribe.subTitle")}</Header.Subheader>
    </Header>
  );
};

const ErrorMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
      <Icon name="delete" color="black" size="massive" />
      <Header as="h2" icon>
        {t("newsletter.errorPage.alreadyUnsubscribed.title")}
        <Header.Subheader>{t("newsletter.errorPage.alreadyUnsubscribed.subTitle")}</Header.Subheader>
        <div style={{ marginTop: "20px" }}>
          <Button primary onClick={() => window.open("/login", "_self")}>
            {t("newsletter.errorPage.alreadyUnsubscribed.loginButton")}
          </Button>
        </div>
      </Header>
    </div>
  );
};

const Callback = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  const [messageType, setMessageType] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const navigateToFirstReminderOrDashboard = async () => {
    try {
      const userId = getUserId();
      const reminders = await getShortReminders(userId);
      if (reminders && reminders.length > 0) {
        const firstReminder = reminders[0];
        const path = `/mein-remindme/angebote/${
          firstReminder.category === "GAS" ? "gas" : firstReminder.subcategory === "HEAT_PUMP" ? "heizstrom" : "strom"
        }/${firstReminder.id}`;
        window.open(path, "_self");
      } else {
        window.open(DashboardRoute, "_self");
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
      window.open(DashboardRoute, "_self");
    }
  };

  const { mutateAsync: subscribe } = useMutation(subscribeToNewsletter, {
    onSuccess: async (response) => {
      saveResponseInLocalStorage(response);
      // setMessageType("SUB_SUCCESS");
      navigateToFirstReminderOrDashboard();
    },
    onError: () => setMessageType("ERROR"),
  });

  const { mutateAsync: unsubscribe } = useMutation(unsubscribeToNewsletter, {
    onSuccess: () => setMessageType("UNSUB_SUCCESS"),
    onError: () => setMessageType("ERROR"),
  });

  const performAction = async (action: string, token: string | null) => {
    setIsLoading(true);
    try {
      if (action === "subscribe" && token) {
        await subscribe(token);
      } else if (action === "unsubscribe" && token) {
        await unsubscribe(token);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const token = query.get("token");

    const handleInitialAction = async () => {
      if (params.action && token) {
        await performAction(params.action, token);
        navigate({ search: "" }, { replace: true });
      }
    };
    // here the reason why we are doing this is because we want to prevent the
    // user from subscribing to the newsletter multiple times as in app we have a check for appVersion
    //  if not match or exists we fetch it and reload the page
    const appVersion = SessionStorage.get("appVersion");
    if (appVersion) {
      handleInitialAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.action, location.search]);

  if (isLoading) {
    return <Spinner />;
  }

  const renderMessage = () => {
    switch (messageType) {
      case "SUB_SUCCESS":
        return <Spinner />;
      case "UNSUB_SUCCESS":
        return <UnsubscribeMessage />;
      case "ERROR":
        return <ErrorMessage />;
      default:
        return params.action === "subscribe" ? (
          <Spinner />
        ) : params.action === "unsubscribe" ? (
          <UnsubscribeMessage />
        ) : (
          <ErrorMessage />
        );
    }
  };

  return <>{renderMessage()}</>;
};

export default Callback;
